import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faCode, faChevronDown, faChevronUp, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { SlidingPills, type TabOption } from './SlidingPills';

interface ConsultedDataViewerProps {
  data: Record<string, any>;
}

// Extracted document interface
interface ExtractedDoc {
  fileName: string;
  fileUrl: string;
  chunks: string[];
}

// Strip legacy "{uuid8}_" prefix from filenames (backward compat with old GCS uploads)
const cleanFileName = (name: string): string =>
  name.replace(/^[0-9a-f]{8}_/i, '');

const ConsultedDataViewer: React.FC<ConsultedDataViewerProps> = ({ data }) => {
  const [activeTab, setActiveTab] = useState<'ui' | 'json'>('ui');
  const [expandedChunks, setExpandedChunks] = useState<Record<number, boolean>>({});

  // Deep extract files/chunks
  const extractedDocs = useMemo(() => {
    const docs: ExtractedDoc[] = [];

    const traverse = (obj: any) => {
      if (!obj || typeof obj !== 'object') return;

      // If object has a file_url or pdf_url, treat it as a document
      const fileUrl = obj.file_url || obj.pdf_url || obj.url;
      const rawName = obj.file_name || obj.name || obj.title || 'Documento clínico';
      const fileName = cleanFileName(rawName);

      if (fileUrl && typeof fileUrl === 'string' && (fileUrl.startsWith('http') || fileUrl.endsWith('.pdf'))) {
        let chunks: string[] = [];
        if (Array.isArray(obj.chunks)) {
          chunks = obj.chunks.map((c: any) => typeof c === 'string' ? c : c.text || c.content || JSON.stringify(c));
        } else if (obj.text || obj.content) {
          chunks = [obj.text || obj.content];
        }

        docs.push({ fileName, fileUrl, chunks });
      }

      // Recurse into Arrays and Objects
      if (Array.isArray(obj)) {
        obj.forEach(traverse);
      } else {
        Object.values(obj).forEach(traverse);
      }
    };

    traverse(data);

    // Deduplicate docs by URL if needed, or just return all
    // Let's filter out docs that have the same URL to prevent UI spam, merging chunks
    const mergedDocs = docs.reduce((acc: ExtractedDoc[], curr: ExtractedDoc) => {
      const existing = acc.find(d => d.fileUrl === curr.fileUrl);
      if (existing) {
        existing.chunks = Array.from(new Set([...existing.chunks, ...curr.chunks]));
      } else {
        acc.push(curr);
      }
      return acc;
    }, []);

    return mergedDocs;
  }, [data]);

  const toggleChunks = (index: number) => {
    setExpandedChunks(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const tabOptions: TabOption[] = useMemo(() => [
    { id: 'ui', label: 'Archivos y Fragmentos', icon: faFilePdf },
    { id: 'json', label: 'JSON en crudo', icon: faCode },
  ], []);

  // If no docs found, fallback to just JSON
  if (extractedDocs.length === 0) {
    return (
      <div className="mt-2 p-3 bg-black/30 rounded-lg text-white/70 overflow-x-auto">
        <pre className="text-[10px]">{JSON.stringify(data, null, 2)}</pre>
      </div>
    );
  }

  return (
    <div className="mt-2 border border-white/10 rounded-lg overflow-hidden bg-black/30">
      {/* Tabs */}
      <div className="flex justify-center p-3 border-b border-white/10 bg-black/75">
        <SlidingPills
          options={tabOptions}
          activeId={activeTab}
          onChange={(id) => setActiveTab(id as 'ui' | 'json')}
          size="sm"
          variant="dark"
        />
      </div>

      <div className="p-3">
        {activeTab === 'json' ? (
          <div className="overflow-x-auto">
            <pre className="text-[10px] text-white/70">{JSON.stringify(data, null, 2)}</pre>
          </div>
        ) : (
          <div className="space-y-3">
            {extractedDocs.map((doc, idx) => (
              <div key={idx} className="bg-black/75 border border-white/5 rounded-lg p-3 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 overflow-hidden flex-1 mr-4">
                    <FontAwesomeIcon icon={faFilePdf} className="text-red-400 text-lg flex-shrink-0" />
                    <span className="text-sm font-medium text-white/90 truncate" title={doc.fileName}>{doc.fileName}</span>
                  </div>
                  <a
                    href={doc.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-taratic px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-full flex items-center gap-2 flex-shrink-0 shadow-lg shadow-blue-500/20 transition-transform active:scale-95"
                  >
                    Ver PDF completo <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </a>
                </div>

                {doc.chunks && doc.chunks.length > 0 && (
                  <div className="mt-1 pt-2 border-t border-white/10">
                    <button
                      onClick={() => toggleChunks(idx)}
                      className="text-xs text-blue-300 hover:text-blue-200 flex items-center gap-1 transition-colors w-full text-left"
                    >
                      {expandedChunks[idx] ? <FontAwesomeIcon icon={faChevronUp} /> : <FontAwesomeIcon icon={faChevronDown} />}
                      {expandedChunks[idx] ? 'Ocultar fragmentos leídos' : `Ver ${doc.chunks.length} fragmento(s) leído(s)`}
                    </button>

                    {expandedChunks[idx] && (
                      <div className="mt-2 space-y-2">
                        {doc.chunks.map((chunk, cIdx) => (
                          <div key={cIdx} className="p-2.5 bg-black/50 border border-white/10 rounded-md text-[11px] text-white/70 font-mono whitespace-pre-wrap leading-relaxed shadow-inner">
                            {chunk}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsultedDataViewer;
